import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { X, Save, Image as ImageIcon, ExternalLink } from 'lucide-react';
import { BugReport, BugStatus, atualizarBugReport } from '../../services/bugReportService';
import { CriticidadeBadge } from './CriticidadeBadge';
import { StatusBadge } from './StatusBadge';
import PermissionGate from '../PermissionGate';

interface Props {
  bugReport: BugReport | null;
  onClose: () => void;
  onUpdated: (updated: BugReport) => void;
}

const STATUS_OPTIONS: BugStatus[] = [
  'ABERTO',
  'EM_ANALISE',
  'EM_CORRECAO',
  'CORRIGIDO',
  'FECHADO',
  'NAO_REPRODUZIVEL',
];

export const BugReportDetailModal: React.FC<Props> = ({ bugReport, onClose, onUpdated }) => {
  const [status, setStatus] = useState<BugStatus>('ABERTO');
  const [resolucao, setResolucao] = useState('');
  const [saving, setSaving] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (bugReport) {
      setStatus(bugReport.status);
      setResolucao(bugReport.resolucao || '');
    }
  }, [bugReport]);

  if (!bugReport) return null;

  const formatDate = (s: string | null) => {
    if (!s) return '—';
    try {
      return format(new Date(s), "dd/MM/yyyy 'as' HH:mm", { locale: ptBR });
    } catch {
      return s;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await atualizarBugReport(bugReport.id, { status, resolucao });
      onUpdated(updated);
      onClose();
    } catch (err) {
      console.error('Erro ao atualizar:', err);
      alert('Erro ao atualizar ocorrencia');
    } finally {
      setSaving(false);
    }
  };

  const isDirty = status !== bugReport.status || resolucao !== (bugReport.resolucao || '');

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
        <div
          className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-3">
              <span className="text-gray-400 font-mono text-sm">#{bugReport.id}</span>
              <h2 className="text-lg font-bold text-gray-900 truncate max-w-md">{bugReport.titulo}</h2>
              <CriticidadeBadge criticidade={bugReport.criticidade} />
              <StatusBadge status={bugReport.status} />
            </div>
            <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-200">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Categoria + Origem */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="block text-xs font-semibold text-gray-500 uppercase mb-0.5">Categoria</span>
                <span className="text-gray-900 font-medium">{bugReport.categoria_nome}</span>
              </div>
              <div>
                <span className="block text-xs font-semibold text-gray-500 uppercase mb-0.5">Origem</span>
                <span className="text-gray-900 font-medium">{bugReport.origem_display}</span>
              </div>
            </div>

            {/* Descricao */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Descricao</h3>
              <div className="bg-gray-50 rounded border border-gray-200 p-3 text-sm text-gray-800 whitespace-pre-wrap">
                {bugReport.descricao}
              </div>
            </div>

            {/* Foto */}
            {bugReport.foto_url && (
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1">
                  <ImageIcon className="w-4 h-4" /> Foto/Evidencia
                </h3>
                <button type="button" onClick={() => setLightboxOpen(true)} className="block">
                  <img
                    src={bugReport.foto_url}
                    alt="Evidencia"
                    className="max-h-64 rounded border border-gray-200 hover:opacity-90 cursor-zoom-in"
                  />
                </button>
                <a
                  href={bugReport.foto_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-1"
                >
                  <ExternalLink className="w-3 h-3" /> Abrir em nova aba
                </a>
              </div>
            )}

            {/* Contexto tecnico */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Contexto Tecnico</h3>
              <div className="grid grid-cols-2 gap-3 bg-blue-50 rounded border border-blue-200 p-3 text-xs">
                <KV label="Dispositivo" value={bugReport.device_model} />
                <KV label="Sistema" value={bugReport.os_version} />
                <KV label="Versao App" value={bugReport.app_version} />
                <KV label="Tela" value={bugReport.tela_atual} />
                <KV label="Hospital" value={bugReport.hospital_nome} />
                <KV label="Contagem" value={bugReport.job_id ? `#${bugReport.job_id}` : null} />
              </div>
            </div>

            {/* Reporter + datas */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="block text-xs font-semibold text-gray-500 uppercase mb-0.5">Reportado por</span>
                <span className="text-gray-900">{bugReport.reportado_por_nome || '—'}</span>
              </div>
              <div>
                <span className="block text-xs font-semibold text-gray-500 uppercase mb-0.5">Data</span>
                <span className="text-gray-900">{formatDate(bugReport.created_at)}</span>
              </div>
            </div>

            <hr />

            {/* Gestao de status */}
            <PermissionGate tela="CONFIGURACOES_VIEW" acao="editar">
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3">Gestao da Ocorrencia</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      value={status}
                      onChange={(e) => setStatus(e.target.value as BugStatus)}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s.replace('_', ' ')}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Descricao da Resolucao
                    </label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="Descreva como foi resolvido (obrigatorio para marcar como Corrigido)..."
                      value={resolucao}
                      onChange={(e) => setResolucao(e.target.value)}
                    />
                  </div>

                  {bugReport.resolvido_em && (
                    <div className="text-xs text-gray-500 bg-green-50 border border-green-200 rounded p-2">
                      Resolvido por <strong>{bugReport.resolvido_por_nome}</strong> em {formatDate(bugReport.resolvido_em)}
                    </div>
                  )}
                </div>
              </div>
            </PermissionGate>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-gray-200 bg-gray-50">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded">
              Fechar
            </button>
            <PermissionGate tela="CONFIGURACOES_VIEW" acao="editar">
              <button
                type="button"
                onClick={handleSave}
                disabled={!isDirty || saving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Salvando...' : 'Salvar alteracoes'}
              </button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && bugReport.foto_url && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <img
            src={bugReport.foto_url}
            alt="Evidencia ampliada"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      )}
    </>
  );
};

const KV: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => (
  <div>
    <span className="block font-semibold text-blue-900/80">{label}</span>
    <span className="text-blue-950">{value || '—'}</span>
  </div>
);
